import React, {useEffect, useState} from 'react';
import {autorun} from "mobx";
import {PauseIcon} from "../../../../assets/icons/PauseIcon";
import {PlayIcon} from "../../../../assets/icons/PlayIcon";
import {TrashIcon} from "../../../../assets/icons/TrashIcon";
import {UndoIcon} from "../../../../assets/icons/UndoIcon";
import {RedoIcon} from "../../../../assets/icons/RedoIcon";
import {RangeIcon} from "../../../../assets/icons/RangeIcon";
import {CompositionService} from "../../../../services/CompositionService";
import {ApplicationStore} from "../../../../store/ApplicationStore";
import {CompositionStateStore} from "../../../../store/CompositionStateStore";
import {TimelineStore} from "../../../../store/TimelineStore";
import {secondsToTimecode} from "../../../../utils/secondsToTimecode";
import './Controls.styles.scss';
import Paper from '../../../../components/Paper/Paper';
import Button from "../../../../components/Button/Button";
import Tooltip from "@mui/material/Tooltip";
import {BiSolidLeftArrow, BiSolidRightArrow} from "react-icons/bi";
import Box from "@mui/material/Box";
import Cursor from "../../../../../pages/Editor/Cursor";
import { UndoManager } from "../../../../services/UndoManager";
import { trackSessionEvent } from "../../../../../analytics/sessionEvents";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SpeedIcon from "@mui/icons-material/Speed";
import {BookmarkBorderOutlined, BookmarkOutlined, KeyboardArrowUpOutlined, KeyboardArrowDownOutlined} from "@mui/icons-material";
import type { ContextMenuOption } from "../../../../components/ContextMenu/ContextMenu";
import { ContextMenuStore } from "../../../../store/ContextMenuStore";
import { ScriptLayerStore } from "../../../../store/ScriptLayerStore";
import { SlipIcon } from "../../../../assets/icons/SlipIcon";

const Controls = ({timelineHidden, toggleTimelineHide, disabled=false}) => {
    const [isPlaying, setIsPlaying] = useState(CompositionStateStore.isPlaying);
    const [displayTime, setDisplayTime] = useState(secondsToTimecode(CompositionStateStore.currentTime, true, true));
    const [displayDuration, setDisplayDuration] = useState(secondsToTimecode(CompositionStateStore.duration, true, true));
    const [selectedLibrettoClipId, setSelectedLibrettoClipId] = useState(ApplicationStore.selectedClipId);
    const [selectedTransitionId, setSelectedTransitionId] = useState(ApplicationStore.selectedTransitionId);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [rangeEnabled, setRangeEnabled] = useState<boolean>(ApplicationStore.rangeToolEnabled);
    const [slipEnabled, setSlipEnabled] = useState<boolean>(ApplicationStore.slipToolEnabled);
    const [zoomPercent, setZoomPercent] = useState<number>(TimelineStore.zoomPercent);

    useEffect(() => {
        const dispose = autorun(() => {
            setIsPlaying(CompositionStateStore.isPlaying);
            setSelectedLibrettoClipId(ApplicationStore.selectedClipId);
            setSelectedTransitionId(ApplicationStore.selectedTransitionId);
            setDisplayTime(secondsToTimecode(CompositionStateStore.currentTime, true, true));
            setDisplayDuration(secondsToTimecode(CompositionStateStore.compositionDuration, true, true));
            setRangeEnabled(ApplicationStore.rangeToolEnabled);
            setSlipEnabled(ApplicationStore.slipToolEnabled);
            setZoomPercent(TimelineStore.zoomPercent);
        });

        return () => dispose();
    }, []);

    useEffect(() => {
        const update = () => {
            setCanUndo(UndoManager.canUndo);
            setCanRedo(UndoManager.canRedo);
        };
        update();
        const unsubscribe = UndoManager.subscribe(update);
        return () => unsubscribe();
    }, []);

    const handlePlay = () => CompositionService.play();
    const handlePause = () => CompositionService.pause();
    const handleTrash = () => CompositionService.deleteLibrettoClipAtCurrentTime();
    const handleSplit = () => CompositionService.splitLibrettoClipAtCurrentTime();
    const decrementZoom = () => TimelineStore.setZoomPercent(TimelineStore.zoomPercent - 25);
    const incrementZoom = () => TimelineStore.setZoomPercent(TimelineStore.zoomPercent + 25);
    const handleStop = () => {
        if (isPlaying) {
            CompositionService.pause();
        }
        CompositionService.seek(0);
    };

    const handleJumpToMarker = (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        const toSeekTimeSec = (srcTimeSec: number): number => {
            const mapped = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(srcTimeSec);
            return mapped != null ? mapped : srcTimeSec / (ScriptLayerStore.playbackRate || 1);
        };

        const markers: any[] = Array.isArray(ScriptLayerStore.transcriptMarkers)
            ? (ScriptLayerStore.transcriptMarkers as any[])
            : [];

        const sorted = [...markers].sort((a, b) => Number(a?.timeSec ?? 0) - Number(b?.timeSec ?? 0));

        const options: ContextMenuOption[] = [];
        options.push({
            title: "Beginning of composition",
            rightText: "Home",
            onClick: () => {
                CompositionService.seek(0);
                ContextMenuStore.hide();
            },
        });

        for (const m of sorted) {
            const id = String(m?.id ?? "");
            if (!id) continue;
            const title = String(m?.title ?? "Marker") || "Marker";
            const timeSec = Number(m?.timeSec ?? 0);
            const seekTime = toSeekTimeSec(timeSec);
            options.push({
                title,
                rightText: secondsToTimecode(seekTime, false, false),
                onClick: () => {
                    CompositionService.seek(seekTime);
                    ContextMenuStore.hide();
                },
            });
        }

        options.push({
            title: "End of composition",
            rightText: "End",
            onClick: () => {
                CompositionService.seek(Number(CompositionStateStore.compositionDuration ?? 0));
                ContextMenuStore.hide();
            },
        });

        const rect = (e.currentTarget as HTMLElement | null)?.getBoundingClientRect?.();
        const x = rect ? rect.left : Number(e.clientX ?? 0);
        const y = rect ? rect.top : Number(e.clientY ?? 0);
        ContextMenuStore.show(x, y, options, rect ? {
            placement: "top-start",
            anchorRect: {
                left: rect.left,
                right: rect.right,
                top: rect.top,
                bottom: rect.bottom,
                width: rect.width,
                height: rect.height,
            },
            widthPx: 320,
            maxWidthPx: 360,
        } : {
            widthPx: 320,
            maxWidthPx: 360,
        });
    };
    const handlePrevScene = () => {
        try {
            const scriptClip: any = (CompositionService as any).getScriptLayerClip?.();
            if (!scriptClip || typeof scriptClip.getScenes !== "function") return;
            const scenes: any[] = Array.isArray(scriptClip.getScenes()) ? scriptClip.getScenes() : [];
            if (scenes.length === 0) return;

            const t = Number(CompositionStateStore.currentTime ?? 0);
            const EPS = 1e-3;

            const srcT = ScriptLayerStore.mapTimelineTimeToSourceTime(t);
            let sceneIndex = -1;

            if (srcT != null && Number.isFinite(srcT)) {
                sceneIndex = scenes.findIndex(
                    (s: any) => srcT >= Number(s?.startSec ?? 0) - EPS && srcT < Number(s?.endSec ?? 0) - EPS,
                );
            }

            // Fallback: resolve scene by mapping scene starts to timeline time.
            if (!Number.isFinite(sceneIndex) || sceneIndex < 0) {
                const mappedStarts = scenes.map((s: any) =>
                    ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(Number(s?.startSec ?? 0)),
                );
                let best = 0;
                for (let i = 0; i < mappedStarts.length; i++) {
                    const start = mappedStarts[i];
                    if (start == null || !Number.isFinite(start)) continue;
                    if (start <= t + EPS) best = i;
                }
                sceneIndex = best;
            }

            // Prev scene: seek to the start of the previous scene. If we're already at the first
            // (or only) scene, seek to 0.
            if (!Number.isFinite(sceneIndex) || sceneIndex <= 0) {
                CompositionService.seek(0);
                return;
            }

            const targetSrc = Number(scenes?.[sceneIndex - 1]?.startSec ?? 0);
            const target = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(targetSrc) ?? 0;
            if (Number.isFinite(target)) CompositionService.seek(target);
        } catch {}
    };
    const handleNextScene = () => {
        try {
            const scriptClip: any = (CompositionService as any).getScriptLayerClip?.();
            if (!scriptClip || typeof scriptClip.getScenes !== "function") return;
            const scenes: any[] = Array.isArray(scriptClip.getScenes()) ? scriptClip.getScenes() : [];
            if (scenes.length === 0) return;

            const t = Number(CompositionStateStore.currentTime ?? 0);
            const EPS = 1e-3;

            const srcT = ScriptLayerStore.mapTimelineTimeToSourceTime(t);
            let sceneIndex = -1;

            if (srcT != null && Number.isFinite(srcT)) {
                sceneIndex = scenes.findIndex(
                    (s: any) => srcT >= Number(s?.startSec ?? 0) - EPS && srcT < Number(s?.endSec ?? 0) - EPS,
                );
            }

            if (!Number.isFinite(sceneIndex) || sceneIndex < 0) {
                const mappedStarts = scenes.map((s: any) =>
                    ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(Number(s?.startSec ?? 0)),
                );
                let best = 0;
                for (let i = 0; i < mappedStarts.length; i++) {
                    const start = mappedStarts[i];
                    if (start == null || !Number.isFinite(start)) continue;
                    if (start <= t + EPS) best = i;
                }
                sceneIndex = best;
            }

            const isLast = !Number.isFinite(sceneIndex) || sceneIndex >= scenes.length - 1;

            // Next scene: seek to the start of the next scene. If we're already at the last
            // (or only) scene, seek to the end of the composition.
            if (isLast) {
                const end = Number(CompositionStateStore.compositionDuration ?? CompositionStateStore.duration ?? 0);
                CompositionService.seek(Number.isFinite(end) ? end : t);
                return;
            }

            const targetSrc = Number(scenes?.[sceneIndex + 1]?.startSec ?? Number.NaN);
            const target = Number.isFinite(targetSrc)
                ? ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(targetSrc) ?? t
                : t;
            if (Number.isFinite(target)) CompositionService.seek(target);
        } catch {}
    };
    const handlePlaybackSpeed = () => {};
    const handleUndo = async () => {
        if (disabled || !UndoManager.canUndo) return;
        await UndoManager.undo();
        trackSessionEvent("undo_redo");
    };
    const handleRedo = async () => {
        if (disabled || !UndoManager.canRedo) return;
        await UndoManager.redo();
        trackSessionEvent("undo_redo");
    };

    const handleRangeClick = () => {
        setRangeEnabled(true);
        ApplicationStore.setRangeToolEnabled(true);
        ApplicationStore.setSlipToolEnabled(false);
    };

    const handleSelectClick = () => {
        setRangeEnabled(false);
        ApplicationStore.setRangeToolEnabled(false);
        ApplicationStore.setSlipToolEnabled(false);
    }

    const handleSlipClick = () => {
        setRangeEnabled(false);
        ApplicationStore.setRangeToolEnabled(false);
        ApplicationStore.setSlipToolEnabled(true);
    };

    const selectEnabled = !rangeEnabled && !slipEnabled;
    const isClipSelected = selectedLibrettoClipId != null;
    const isTransitionSelected = selectedTransitionId != null;
    const isSplitEnabled = isClipSelected;
    const isRangeToolEnabled = isClipSelected;
    const isTrashEnabled = isClipSelected || isTransitionSelected;

    return (
        <Box>
            <div className="controls">
                <div className="controls__section">
                    <Box
                        onClick={disabled ? undefined : toggleTimelineHide}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            px: 1,
                            py: 0.375,
                            borderRadius: '6px',
                            cursor: disabled ? 'default' : 'pointer',
                            opacity: disabled ? 0.4 : 1,
                            color: 'var(--app-text-muted)',
                            transition: 'background-color 0.15s ease, color 0.15s ease',
                            userSelect: 'none',
                            '&:hover': disabled ? {} : {
                                backgroundColor: 'var(--app-bg-subtle)',
                                color: 'var(--app-text-primary)',
                            },
                        }}
                    >
                        {timelineHidden ? (
                            <KeyboardArrowUpOutlined sx={{ fontSize: 16 }} />
                        ) : (
                            <KeyboardArrowDownOutlined sx={{ fontSize: 16 }} />
                        )}
                        <Box
                            component="span"
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '12px',
                                fontWeight: 500,
                                lineHeight: 1,
                            }}
                        >
                            {timelineHidden ? 'Show timeline' : 'Hide timeline'}
                        </Box>
                    </Box>

                    {/*<Button*/}
                    {/*    data-action="history"*/}
                    {/*    onClick={handleUndo}*/}
                    {/*    disabled={disabled || !canUndo}*/}
                    {/*    title="Undo"*/}
                    {/*>*/}
                    {/*    <UndoIcon />*/}
                    {/*</Button>*/}
                    {/*<Button*/}
                    {/*    data-action="history"*/}
                    {/*    onClick={handleRedo}*/}
                    {/*    disabled={disabled || !canRedo}*/}
                    {/*    title="Redo"*/}
                    {/*>*/}
                    {/*    <RedoIcon />*/}
                    {/*</Button>*/}

                    {/*<Button disabled={!isTrashEnabled} onClick={handleTrash}>*/}
                    {/*  <TrashIcon />*/}
                    {/*</Button>*/}
                </div>

                <div className="controls__section">
                    {/*<Button onClick={handleStop} disabled={disabled}>*/}
                    {/*    <div className="controls__section--stop-btn"></div>*/}
                    {/*</Button>*/}

                    <span className="controls__playback__time">{displayTime} / {displayDuration}</span>

                    <Button onClick={handlePrevScene} disabled={disabled} title="Previous scene">
                        <SkipPreviousIcon />
                    </Button>

                    {isPlaying ? (
                        <Button data-tour="timeline-play-toggle" onClick={handlePause} disabled={disabled}>
                            <PauseIcon/>
                        </Button>
                    ) : (
                        <Button data-tour="timeline-play-toggle" onClick={handlePlay} disabled={disabled}>
                            <PlayIcon/>
                        </Button>
                    )}

                    <Button onClick={handleNextScene} disabled={disabled} title="Next scene">
                        <SkipNextIcon />
                    </Button>

                    {/*<Button onClick={handlePlaybackSpeed} disabled={disabled} title="Playback speed">*/}
                    {/*    <SpeedIcon />*/}
                    {/*</Button>*/}

                    <Button onClick={handleJumpToMarker} disabled={disabled} title="Jump to marker">
                        <BookmarkBorderOutlined />
                    </Button>

                    {/*<Button onClick={() => {*/}
                    {/*    CompositionService.previousFrame()*/}
                    {/*}}>*/}
                    {/*    <BiSolidLeftArrow/>*/}
                    {/*</Button>*/}
                    {/*<Button onClick={() => {CompositionService.nextFrame()}}>*/}
                    {/*    <BiSolidRightArrow/>*/}
                    {/*</Button>*/}
                </div>

                <div className="controls__section">

                    {!timelineHidden && (
                        <>
                            <div className="controls__zoom-group" role="group" aria-label="Timeline zoom">
                                <Button
                                    disabled={disabled || zoomPercent <= 50}
                                    onClick={decrementZoom}
                                    variant="ghost"
                                    className="controls__zoom-btn"
                                    title={"Zoom out"}
                                >
                                    <RemoveIcon fontSize="small" />
                                </Button>

                                <div className="controls__zoom-value" aria-label="Zoom level">
                                    {zoomPercent}%
                                </div>

                                <Button
                                    disabled={disabled || zoomPercent >= 1000}
                                    onClick={incrementZoom}
                                    variant="ghost"
                                    className="controls__zoom-btn"
                                    title={"Zoom in"}
                                >
                                    <AddIcon fontSize="small" />
                                </Button>
                            </div>

                            <div className="controls__tool-group" role="group" aria-label="Timeline tools">
                            <Button
                                disabled={disabled}
                                onClick={handleSelectClick}
                                variant="ghost"
                                className={`controls__tool-btn ${selectEnabled && !disabled ? 'controls__tool-btn--active' : ''}`}
                                title={"Select"}
                            >
                                <Cursor/>
                            </Button>

                            <Button
                                disabled={disabled}
                                onClick={handleSlipClick}
                                variant="ghost"
                                className={`controls__tool-btn ${slipEnabled ? 'controls__tool-btn--active' : ''}`}
                                title={"Slip"}
                            >
                                <SlipIcon />
                            </Button>

                            <Button
                                disabled={disabled}
                                onClick={handleRangeClick}
                                variant="ghost"
                                className={`controls__tool-btn ${rangeEnabled ? 'controls__tool-btn--active' : ''}`}
                                title={"Range"}
                            >
                                <RangeIcon/>
                            </Button>
                            </div>
                        </>
                    )}

                </div>
            </div>
        </Box>
    );
};

export default Controls;
